import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function CheckUpIndex() {
    const { t } = useContext(TranslatorContext);

    const [checkUps, setCheckUps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentId, setCurrentId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({ name: '', price: '', home_visit_price: '' });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({ id: null, name: '', price: '', home_visit_price: '' });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        fetchCheckUps();
    }, []);

    const fetchCheckUps = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/check-ups`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                setCheckUps(res.data.checkUps || []);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    // Store Modal Functions
    const openStoreModal = () => {
        setStoreForm({ name: '', price: '', home_visit_price: '' });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/check-ups`,
                {
                    name: storeForm.name,
                    price: storeForm.price,
                    home_visit_price: storeForm.home_visit_price
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.checkUp) {
                fetchCheckUps();
                setStoreModal(false);
                setStoreForm({ name: '', price: '', home_visit_price: '' });

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Check-Up created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating check-up:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create check-up'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Modal Functions
    const openUpdateModal = (item) => {
        setUpdateForm({
            id: item.id,
            name: item.name,
            price: item.price || '',
            home_visit_price: item.home_visit_price || ''
        });
        setUpdateErrors({});
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/check-ups-update/${updateForm.id}`,
                {
                    name: updateForm.name,
                    price: updateForm.price,
                    home_visit_price: updateForm.home_visit_price
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.checkUp) {
                fetchCheckUps();
                setUpdateModal(false);

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Check-Up updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateErrors({ general: [error.response.data.message] });

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.message || t('Check-Up not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating check-up:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update check-up'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Delete Function - UPDATED (accepts id parameter)
    const handleDelete = async (id) => {
        setDeleteLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/check-ups-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchCheckUps();

            Swal.fire({
                icon: 'success',
                title: t('deleted'),
                text: t('Check-Up deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting check-up:', error);

            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setDeleteLoading(false);
            setCurrentId(null);
        }
    };

    // Delete confirmation with SweetAlert - UPDATED
    const confirmDelete = (id) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("Want to delete this check-up?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete(id); // Pass ID directly instead of using state
            }
        });
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 style={{ color: "#777" }}>Check-Up Services</h4>
                <ButtonComponent
                    className="btn"
                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                    onClick={openStoreModal}
                >
                    + Add Check-Up
                </ButtonComponent>
            </div>

            <div className="card-container">
                <div className="p-4">
                    {checkUps.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                            <p className="mt-2">No check-up services found</p>
                            <ButtonComponent
                                className="btn btn-sm mt-2"
                                style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                onClick={openStoreModal}
                            >
                                + Add First Check-Up
                            </ButtonComponent>
                        </div>
                    ) : (
                        <Table responsive hover borderless>
                            <thead>
                                <tr className="table-header">
                                    <th>Service Name</th>
                                    <th>Clinic Price</th>
                                    <th>Home Visit Price</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checkUps.map((checkUp) => (
                                    <tr key={checkUp.id} className="table-row">
                                        <td className="align-middle">
                                            <i
                                                className="material-icons delete-icon"
                                                onClick={() => confirmDelete(checkUp.id)}
                                                style={{ cursor: 'pointer', marginRight: '12px', fontSize: '18px', color: '#dc3545' }}
                                            >delete</i>
                                            {checkUp.name}
                                        </td>
                                        <td className="align-middle">
                                            {checkUp.price ? `${checkUp.price}` : '-'}
                                        </td>
                                        <td className="align-middle">
                                            {checkUp.home_visit_price ? `${checkUp.home_visit_price}` : '-'}
                                        </td>
                                        <td className="text-end align-middle">
                                            <button
                                                className="action-btn"
                                                onClick={() => openUpdateModal(checkUp)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#3182ce'
                                                }}
                                            >
                                                <i className="material-icons" style={{ fontSize: "16px" }}>edit_note</i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>

            {/* Store Modal - Add Check-Up */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Check-Up Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={storeForm.name}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.name}
                                placeholder="Enter check-up name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Clinic Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="price"
                                className="custom-input"
                                value={storeForm.price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.price}
                                placeholder="Enter clinic price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Price for clinic visit</small>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Home Visit Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="home_visit_price"
                                className="custom-input"
                                value={storeForm.home_visit_price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.home_visit_price}
                                placeholder="Enter home visit price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.home_visit_price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Price for home visit service</small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={storeLoading}
                        >
                            {storeLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Modal - Edit Check-Up */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Check-Up Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateForm.name}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.name}
                                placeholder="Enter check-up name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Clinic Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="price"
                                className="custom-input"
                                value={updateForm.price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.price}
                                placeholder="Enter clinic price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Home Visit Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="home_visit_price"
                                className="custom-input"
                                value={updateForm.home_visit_price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.home_visit_price}
                                placeholder="Enter home visit price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.home_visit_price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateLoading}
                        >
                            {updateLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}